import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Plus, Pencil, Trash2, ExternalLink, Star } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ImageUpload from '../../components/ImageUpload';
import { serviceIcons } from '../../components/ServiceCard';
import { CharCount, GooglePreview } from '../../components/admin/GooglePreview';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LANGS = [['de', 'Deutsch', ''], ['en', 'English', '/en'], ['es', 'Español', '/es']];
const TEXT_FIELDS = ['title', 'subtitle', 'h1', 'description', 'seo_title', 'seo_description', 'keyword'];

const emptyService = () => {
  const s = { key: '', slug: '', icon: 'Building2', image: '', order: 0, active: true, featured: false };
  LANGS.forEach(([l]) => {
    TEXT_FIELDS.forEach(f => { s[`${f}_${l}`] = ''; });
    ['bullets', 'process', 'faqs'].forEach(f => { s[`${f}_${l}`] = ''; });
  });
  return s;
};

const toLines = (arr, fmt) => (arr || []).map(fmt).join('\n');
const fromLines = (text, parse) => (text || '').split('\n').map(l => l.trim()).filter(Boolean).map(parse);
const pair = (line, a, b) => {
  const [x, ...rest] = line.split('|');
  return { [a]: x.trim(), [b]: rest.join('|').trim() };
};

const toForm = (item) => {
  const f = { ...emptyService(), ...item };
  LANGS.forEach(([l]) => {
    f[`bullets_${l}`] = toLines(item[`bullets_${l}`], x => x);
    f[`process_${l}`] = toLines(item[`process_${l}`], s => `${s.title} | ${s.text}`);
    f[`faqs_${l}`] = toLines(item[`faqs_${l}`], q => `${q.q} | ${q.a}`);
  });
  return f;
};

const toPayload = (f) => {
  const p = { ...f };
  delete p.id;
  delete p.updated_at;
  LANGS.forEach(([l]) => {
    p[`bullets_${l}`] = fromLines(f[`bullets_${l}`], x => x);
    p[`process_${l}`] = fromLines(f[`process_${l}`], x => pair(x, 'title', 'text'));
    p[`faqs_${l}`] = fromLines(f[`faqs_${l}`], x => pair(x, 'q', 'a'));
  });
  return p;
};

function Field({ label, hint, counter, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs">{label}</Label>
        {counter}
      </div>
      {children}
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function LangFields({ form, lang, prefix, update }) {
  const v = (f) => form[`${f}_${lang}`] || '';
  const bind = (f) => ({ value: v(f), onChange: e => update(`${f}_${lang}`, e.target.value), 'data-testid': `service-${f}-${lang}` });
  const url = `${window.location.origin}${prefix}/leistungen/${form.slug || form.key || 'slug'}`;
  return (
    <div className="space-y-4 pt-2">
      <div className="grid grid-cols-2 gap-3">
        <Field label={"Titel (Karte & Menü)"}><Input {...bind('title')} /></Field>
        <Field label={"Slogan"}><Input {...bind('subtitle')} placeholder="z.B. Effizient planen. Sicher umsetzen." /></Field>
      </div>
      <Field label={"H1-Überschrift der Unterseite"} hint={"Hauptüberschrift für Google – möglichst mit Ihrem Suchbegriff."}><Input {...bind('h1')} /></Field>
      <Field label={"Beschreibung"} counter={<CharCount value={v('description')} max={450} />}><Textarea rows={4} {...bind('description')} /></Field>
      <Field label={"Leistungspunkte"} hint={"Ein Punkt pro Zeile."}><Textarea rows={6} {...bind('bullets')} /></Field>
      <Field label={"Ablauf (Unterseite)"} hint={"Pro Zeile: Schritt-Titel | Beschreibung"}><Textarea rows={4} {...bind('process')} /></Field>
      <Field label={"Häufige Fragen (Unterseite)"} hint={"Pro Zeile: Frage | Antwort"}><Textarea rows={4} {...bind('faqs')} /></Field>
      <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-4 space-y-3">
        <p className="text-sm font-semibold text-[#0F172A]">Suchmaschinen (SEO)</p>
        <Field label={"SEO-Titel"} counter={<CharCount value={v('seo_title')} min={30} max={60} />}><Input {...bind('seo_title')} /></Field>
        <Field label={"Meta-Beschreibung"} counter={<CharCount value={v('seo_description')} min={110} max={160} />}><Textarea rows={2} {...bind('seo_description')} /></Field>
        <Field label={"Fokus-Keyword"} hint={"Begriff, unter dem diese Seite gefunden werden soll – wird im SEO-Cockpit geprüft."}><Input {...bind('keyword')} /></Field>
        <GooglePreview url={url} title={v('seo_title') || v('title')} description={v('seo_description') || v('description')} />
      </div>
    </div>
  );
}

function GeneralFields({ form, update }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Field label={"Schlüssel"}><Input value={form.key} onChange={e => update('key', e.target.value)} placeholder="z.B. hallenmontage" data-testid="service-key" /></Field>
        <Field label={"URL-Slug"} hint={`/leistungen/${form.slug || '…'}`}><Input value={form.slug} onChange={e => update('slug', e.target.value)} placeholder="hallenmontage" data-testid="service-slug" /></Field>
        <Field label={"Reihenfolge"}><Input type="number" value={form.order} onChange={e => update('order', parseInt(e.target.value) || 0)} /></Field>
      </div>
      <div className="grid grid-cols-3 gap-3 items-end">
        <Field label={"Icon"}>
          <select value={form.icon} onChange={e => update('icon', e.target.value)} className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm" data-testid="service-icon">
            {Object.keys(serviceIcons).map(name => <option key={name} value={name}>{name}</option>)}
          </select>
        </Field>
        <label className="flex items-center gap-2 h-9"><Switch checked={form.active} onCheckedChange={v => update('active', v)} data-testid="service-active" /><span className="text-xs">Aktiv</span></label>
        <label className="flex items-center gap-2 h-9"><Switch checked={form.featured} onCheckedChange={v => update('featured', v)} data-testid="service-featured" /><span className="text-xs">Hervorgehoben (blauer Rahmen)</span></label>
      </div>
      <ImageUpload value={form.image} onChange={v => update('image', v)} label="Bild der Unterseite" />
    </div>
  );
}

export default function AdminServices() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyService());
  const token = localStorage.getItem('itcs-admin-token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => axios.get(`${API}/admin/services`, { headers })
    .then(r => setItems([...r.data].sort((a, b) => (b.active - a.active) || (a.order - b.order)))).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyService()); setEditing(null); setDialogOpen(true); };
  const openEdit = (item) => { setForm(toForm(item)); setEditing(item.id); setDialogOpen(true); };
  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    try {
      if (editing) await axios.put(`${API}/admin/services/${editing}`, toPayload(form), { headers });
      else await axios.post(`${API}/admin/services`, toPayload(form), { headers });
      toast.success(editing ? 'Leistung aktualisiert' : 'Leistung erstellt');
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Speichern'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Leistung wirklich löschen?')) return;
    try { await axios.delete(`${API}/admin/services/${id}`, { headers }); toast.success('Gelöscht'); load(); }
    catch { toast.error('Fehler'); }
  };

  return (
    <div data-testid="admin-services">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">Leistungen verwalten</h1>
          <p className="text-sm text-slate-500 mt-1">Jede aktive Leistung erscheint auf der Startseite, im Menü und erhält eine eigene, SEO-optimierte Unterseite.</p>
        </div>
        <Button onClick={openNew} className="bg-[#066ce7] hover:bg-[#005bb5] text-white" data-testid="add-service-btn">
          <Plus className="w-4 h-4 mr-2" /> Neue Leistung
        </Button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Reihenfolge</TableHead><TableHead>Titel (DE)</TableHead><TableHead>Unterseite</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aktionen</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map(s => (
              <TableRow key={s.id} data-testid={`service-row-${s.key}`}>
                <TableCell>{s.order}</TableCell>
                <TableCell className="font-medium">
                  <span className="inline-flex items-center gap-2">{s.title_de}{s.featured && <Star className="w-3.5 h-3.5 text-[#066ce7] fill-[#066ce7]" />}</span>
                </TableCell>
                <TableCell>
                  {s.slug ? (
                    <a href={`/leistungen/${s.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-[#066ce7] hover:underline font-mono">
                      /leistungen/{s.slug} <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : <span className="text-xs text-slate-400">–</span>}
                </TableCell>
                <TableCell>{s.active ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Aktiv</Badge> : <Badge variant="outline">Inaktiv</Badge>}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`edit-service-${s.key}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(s.id)} className="text-red-500" data-testid={`delete-service-${s.key}`}><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="service-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Leistung bearbeiten' : 'Neue Leistung'}</DialogTitle></DialogHeader>
          <GeneralFields form={form} update={update} />
          <Tabs defaultValue="de" className="mt-2">
            <TabsList>
              {LANGS.map(([l, name]) => <TabsTrigger key={l} value={l} data-testid={`service-tab-${l}`}>{name}</TabsTrigger>)}
            </TabsList>
            {LANGS.map(([l, , prefix]) => (
              <TabsContent key={l} value={l}><LangFields form={form} lang={l} prefix={prefix} update={update} /></TabsContent>
            ))}
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} className="bg-[#066ce7] text-white" data-testid="save-service-btn">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
